// Isolated WebRTC mesh: each participant has separate camera/audio and screen tracks.
export class VideoMesh {
  constructor({ selfId, stream, iceServers, send, onRemote, onConnection, onError }) {
    Object.assign(this, { selfId, stream, iceServers, send, onRemote, onConnection, onError });
    this.peers = new Map();
    this.screen = null;
    this.closed = false;
  }
  peer(id) {
    if (this.peers.has(id)) return this.peers.get(id);
    const pc = new RTCPeerConnection({ iceServers: this.iceServers });
    const peer = { pc, makingOffer: false, ignoreOffer: false, settingAnswer: false, pending: [], polite: this.selfId > id, camera: new MediaStream(), screen: new MediaStream(), failures: 0 };
    this.peers.set(id, peer);
    peer.audioTransceiver = pc.addTransceiver(this.stream.getAudioTracks()[0], { direction: 'sendrecv', streams: [this.stream] });
    peer.cameraTransceiver = pc.addTransceiver(this.stream.getVideoTracks()[0], { direction: 'sendrecv', streams: [this.stream] });
    peer.remoteMedia = {};
    peer.remoteTracks = new Map();
    if (this.screen) this.attachScreen(peer, this.screen);
    const publishTracks = () => {
      if (this.closed || pc.connectionState === 'closed') return;
      const cameraTracks = [], screenTracks = [];
      for (const [mid, track] of peer.remoteTracks) {
        if (track.readyState === 'ended') continue;
        if (mid === peer.remoteMedia.screen) screenTracks.push(track);
        else if (mid === peer.remoteMedia.camera || mid === peer.remoteMedia.audio) cameraTracks.push(track);
      }
      // Fresh stream objects rebind video elements when a late screen track arrives.
      peer.camera = new MediaStream(cameraTracks);
      peer.screen = new MediaStream(screenTracks);
      this.onRemote(id, { camera: peer.camera, screen: peer.screen });
    };
    peer.publishTracks = publishTracks;
    pc.ontrack = event => {
      peer.remoteTracks.set(event.transceiver.mid, event.track);
      event.track.onunmute = publishTracks;
      event.track.onended = publishTracks;
      publishTracks();
    };
    pc.onicecandidate = ({ candidate }) => {
      if (candidate && !this.closed) this.send(id, { candidate: candidate.toJSON() }).catch(this.onError);
    };
    pc.onnegotiationneeded = async () => {
      try {
        peer.makingOffer = true;
        await pc.setLocalDescription();
        if (!this.closed) await this.send(id, this.description(peer));
      } catch (error) { if (!this.closed) this.onError(error); }
      finally { peer.makingOffer = false; }
    };
    pc.onconnectionstatechange = () => {
      this.onConnection(id, pc.connectionState);
      if (pc.connectionState === 'failed' && peer.failures++ < 2) pc.restartIce();
    };
    return peer;
  }
  async receive(id, message) {
    if (this.closed) return;
    const peer = this.peer(id);
    const pc = peer.pc;
    if (message.description) {
      const description = message.description;
      const ready = !peer.makingOffer && (pc.signalingState === 'stable' || peer.settingAnswer);
      const collision = description.type === 'offer' && !ready;
      peer.ignoreOffer = !peer.polite && collision;
      if (peer.ignoreOffer) { peer.pending = []; return; }
      peer.remoteMedia = message.media || {};
      peer.settingAnswer = description.type === 'answer';
      try { await pc.setRemoteDescription(description); } finally { peer.settingAnswer = false; }
      peer.publishTracks();
      for (const candidate of peer.pending.splice(0)) await pc.addIceCandidate(candidate);
      if (description.type === 'offer') {
        await pc.setLocalDescription();
        await this.send(id, this.description(peer));
      }
    } else if (message.candidate && !peer.ignoreOffer) {
      if (pc.remoteDescription) await pc.addIceCandidate(message.candidate);
      else peer.pending.push(message.candidate);
    }
  }
  sync(participants) {
    const ids = new Set(participants.filter(p => p.id !== this.selfId).map(p => p.id));
    for (const id of ids) this.peer(id);
    for (const [id, peer] of this.peers) if (!ids.has(id)) { peer.pc.close(); this.peers.delete(id); this.onRemote(id, null); }
  }
  description(peer) {
    const media = {};
    for (const [kind, transceiver] of [['audio', peer.audioTransceiver], ['camera', peer.cameraTransceiver], ['screen', peer.screenTransceiver]]) {
      if (transceiver && transceiver.mid != null) media[kind] = transceiver.mid;
    }
    return { description: { type: peer.pc.localDescription.type, sdp: peer.pc.localDescription.sdp }, media };
  }
  attachScreen(peer, track) {
    // Add a real sending track, with a stream, so browsers negotiate its RTP stream.
    // A pre-negotiated empty video slot may remain receive-only after negotiation.
    peer.screenTransceiver = peer.pc.addTransceiver(track, { direction: 'sendonly', streams: [new MediaStream([track])] });
    peer.screenSender = peer.screenTransceiver.sender;
  }
  async share(track) {
    const previous = this.screen;
    try {
      await Promise.all(Array.from(this.peers.values()).map(async peer => {
        if (!peer.screenSender) {
          if (track) this.attachScreen(peer, track);
          return;
        }
        try { await peer.screenSender.replaceTrack(track); }
        catch (error) {
          if (!track || error.name !== 'InvalidModificationError') throw error;
          // Renegotiate if a new capture resolution/codec cannot replace this sender.
          peer.screenTransceiver.stop();
          this.attachScreen(peer, track);
        }
      }));
      this.screen = track;
    } catch (error) {
      await Promise.allSettled(Array.from(this.peers.values()).filter(p => p.screenSender).map(p => p.screenSender.replaceTrack(previous)));
      throw error;
    }
  }
  close() {
    this.closed = true;
    for (const peer of this.peers.values()) peer.pc.close();
    this.peers.clear();
    this.stream.getTracks().forEach(track => track.stop());
    if (this.screen) this.screen.stop();
  }
}
